// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, useTheme } from '@mui/system';
import { Typography, Button, LinearProgress, IconButton, Dialog, DialogContent, DialogTitle, DialogActions, Grid, Tooltip } from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import CheckIcon from '@mui/icons-material/Check';
import ErrorIcon from '@mui/icons-material/Error';
import CloseIcon from '@mui/icons-material/Close';
import { useAppDispatch, useAppSelector } from 'Hooks';
import uploadFilesIcon from 'Assets/upload-files-icon.png';
import DropzoneWrapper from 'Components/dropzone';
import stylesDef from './styles';
import { DeleteUploadedFileThunk, FileUploadCancelThunk, FileUploadStartThunk } from 'Features/fileUpload/fileUploadThunk';
import { SetFileUploadSizeErrorAction } from 'Features/fileUpload/fileUploadActions';
import JSZip from 'jszip';
import ErrorDialog from 'Components/ErrorDialog';
import ConfirmDialog from 'Components/ConfirmDialog';
import ZipTypeDialog, { ZipType } from 'Components/ZipTypeDialog';
import { normalizeZipEntries, buildNormalizedZip } from './normalizeZipEntries';

const MultipleFileUploadForm = ({ onSuccess }) => {
  const theme = useTheme();
  const styles = stylesDef(theme);
  const dispatch = useAppDispatch();
  const [openDialog, setOpenDialog] = useState(false);
  const [showUnsupportedFiletypesDialog, setShowUnsupportedFiletypesDialog] = useState(false);
  const [showNoFilesDialog, setShowNoFilesDialog] = useState(false);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const { resources } = useAppSelector((state) => state.auth);
  const { projectId } = useAppSelector((state) => state.project);
  const { uploadFiles, uploadFileSizeError } = useAppSelector((state) => state.fileUpload);
  const allFilesUploaded = uploadFiles.filter((f) => f.progress < 100).length;
  const editorFunctions = !_ADMIN_ ? 'editorFunctions' in resources : true;
  const placeOrder = !_ADMIN_ ? 'placeOrder' in resources : true;
  const [showConfirmDialog, setShowConfirmDialog] = useState<boolean>(false);
  const [newKmlFile, setNewKmlFile] = useState<File | null>(null);
  const [existingKmlFile, setExistingKmlFile] = useState<IFile | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  // When a zip is uploaded we ask the user whether it contains pole tag images or panoramas before starting the upload
  const [showZipTypeDialog, setShowZipTypeDialog] = useState<boolean>(false);
  const [zipFilesToUpload, setZipFilesToUpload] = useState<File[]>([]);

  // Allow Airworks admin to upload any files and restrict file types for platform users based on permissions
  let acceptedExtensionsForPlatformUsers;

  if (editorFunctions && placeOrder) {
    acceptedExtensionsForPlatformUsers = [
      '.tiff',
      '.tif',
      '.las',
      '.laz',
      '.kml',
      '.kmz',
      '.zip',
      // '.dxf'
    ];
  } else if (!editorFunctions && placeOrder) {
    acceptedExtensionsForPlatformUsers = ['.tiff', '.tif', '.las', '.laz', '.kml', '.kmz', '.zip'];
  } else if (editorFunctions && !placeOrder) {
    acceptedExtensionsForPlatformUsers = ['.tiff', '.tif'];
  }

  // Check if Airworks Admin and assign accepted extensions accordingly
  const acceptedExtensions = _ADMIN_ ? ['.tiff', '.tif', '.kml', '.kmz', '.laz', '.zip'] : acceptedExtensionsForPlatformUsers;
  const getBackgroundByExtension = (extension: string) => {
    switch (extension) {
    case 'las': return styles.backgroundAqua;
    case 'dxf': return styles.backgroundAqua;
    case 'laz': return styles.backgroundAqua;
    case 'kmz': case 'kml': return styles.backgroundGold;
    default: return styles.backgroundMediumBlue;
    }
  };

  const messagingWithout2dEditorPermissions = `Upload 2D data (.tif or .tiff) and/or point cloud data (.las or .laz) for one site
    to get started. Multiple .tif, .tiff, .las, and .laz are permitted. .kml and .kmz files are also welcome.`;

  const messagingWith2dEditorPermissions = 'Upload 2D data (.tif or .tiff) to get started with your CAD drawing';
  const messagingForFileUpload = editorFunctions ? messagingWith2dEditorPermissions : messagingWithout2dEditorPermissions

  const cancelUploadOrDeleteFile = (progress: number, id: string, fileName: string) => {
    if (progress === 100) {
      dispatch(DeleteUploadedFileThunk(id, fileName));
    } else {
      dispatch(FileUploadCancelThunk(id));
    }
  };
  useEffect(() => {
    if (uploadFileSizeError) {
      setOpenDialog(true);
      dispatch(SetFileUploadSizeErrorAction(false));
    }
  }, [uploadFileSizeError]);

  // If the batch contains a .zip, ask the user whether it's pole tag images or panoramas before uploading.
  // Otherwise start the upload immediately. The selected type only affects how zip files are routed server-side.
  const startUpload = async (files: File[]) => {
    if (files.some((file) => file.name.endsWith('.zip'))) {
      setZipFilesToUpload(files);
      setShowZipTypeDialog(true);
    } else {
      await dispatch(FileUploadStartThunk(files));
    }
  };

  const handleZipTypeCancel = () => {
    setShowZipTypeDialog(false);
    setZipFilesToUpload([]);
  };

  const handleZipTypeConfirm = async (zipType: ZipType) => {
    setShowZipTypeDialog(false);
    const files = zipFilesToUpload;
    setZipFilesToUpload([]);
    const fileType = zipType === 'poletagimage' ? 'poletagimage' : '';
    await dispatch(FileUploadStartThunk(files, false, false, false, false, false, false, fileType));
  };

  const handleFileUpload = async (acceptedFilesArray: File[], skipKmlCheck: boolean = false) => {
    let filesToUpload: File[] = acceptedFilesArray;
    let pendingFiles: File[] = [];

    for (const _file of acceptedFilesArray) {
      // Check if the file being uploaded is a KML file (skip if this is a replacement)
      if (!skipKmlCheck && (_file.name.endsWith('.kml') || _file.name.endsWith('.kmz'))) {
        // Check if there are already other KML files uploaded
        const existingKmlFiles = uploadFiles.filter(file => 
          (file.extension === 'kml' || file.extension === 'kmz')
        );
        
        if (existingKmlFiles.length > 0) {
          const oldKmlFile = existingKmlFiles[0];
          setNewKmlFile(_file);
          setExistingKmlFile(oldKmlFile);
          setShowConfirmDialog(true);
          // Return early to prevent further processing until user confirms replacement
          return;
        }
      }

      if (_file.name.endsWith('.zip')) {
        // we check if .zip file is larger than 5GB
        const maxSizeInBytes = 5 * 1024 * 1024 * 1024; // 5GB
        if (_file.size > maxSizeInBytes) {
          filesToUpload = filesToUpload.filter((file) => file.name !== _file.name);
          pendingFiles.push(_file);
          continue; // Skip validation for this file
        }
        const zip = await new JSZip().loadAsync(await _file.arrayBuffer());

        // Zips made on macOS carry entries the user never sees (__MACOSX/, ._ resource forks, .DS_Store) and often
        // a wrapper folder. Those are dropped / flattened silently and only the real payload is validated
        // (.jpg, .jpeg, .txt, case-insensitive, no nested folders). See normalizeZipEntries.ts.
        const normalized = normalizeZipEntries(zip.files);

        if (normalized.unsupported.length !== 0) {
          // Genuinely unsupported payload (other extensions or nested folders): ask the user.
          // The proceed handler re-normalizes the original file and keeps only the supported entries.
          filesToUpload = filesToUpload.filter((file) => file !== _file);
          pendingFiles.push(_file);
        } else if (normalized.supported.length === 0) {
          // Nothing but artifacts inside: there is no payload to upload at all.
          filesToUpload = filesToUpload.filter((file) => file !== _file);
          setShowNoFilesDialog(true);
        } else if (normalized.needsRezip) {
          // Only invisible entries were removed (or one wrapper folder flattened): re-zip under the same
          // filename without showing the error modal, since the user never saw those entries.
          const newZipBlob = await buildNormalizedZip(zip, normalized.supported).generateAsync({ type: 'blob' });
          const newFile = new File([newZipBlob], _file.name, { type: 'application/zip' });
          filesToUpload = filesToUpload.map((file) => (file === _file ? newFile : file));
        }
      }
    }
    
    if (pendingFiles.length !== 0) {
      // Show dialog to user about unsupported files
      setPendingFiles(pendingFiles);
      setShowUnsupportedFiletypesDialog(true);
    }
    if (filesToUpload.length !== 0) {
      await startUpload(filesToUpload);
    }
  };

  const handleProceedUnsupportedFiletypes = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.stopPropagation();
    // In here we generate a new zip file with only supported files and replace the original file with the new zip file
    let finalFiles: File[] = [];

    for (const _file of pendingFiles) {
      if (_file.name.endsWith('.zip')) {
        // If the file is a zip file, we need to extract the supported files
        const zip = await new JSZip().loadAsync(await _file.arrayBuffer());
        // Same rules as the validation step: keep the supported payload, drop everything else
        const { supported } = normalizeZipEntries(zip.files);

        if (supported.length > 0) {
          // If we have supported files, we can create a new zip
          const newZipBlob = await buildNormalizedZip(zip, supported).generateAsync({ type: 'blob' });
          const newZipFile = new File([newZipBlob], `${_file.name.split('.zip')[0]}_extracted.zip`, { type: 'application/zip' });
          finalFiles.push(newZipFile);
        }
      }
    }
    
    setShowUnsupportedFiletypesDialog(false);
    if (finalFiles.length !== 0) {
      await startUpload(finalFiles);
    } else {
      setShowNoFilesDialog(true);
    }
  }

  const handleUploadDifferentFiles = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.stopPropagation();
    // In here we remove the panorama files from the uploadFiles state
    setPendingFiles([]);
    setShowUnsupportedFiletypesDialog(false);
  }

  const onOk = async () => {
    if (!existingKmlFile || !newKmlFile) {
      return;
    }

    // Delete the existing KML file
    setLoading(true);
    await dispatch(DeleteUploadedFileThunk(existingKmlFile.id, existingKmlFile.name));
    setShowConfirmDialog(false);
    setLoading(false);
    // Upload the new KML file
    await handleFileUpload([newKmlFile], true);
    setNewKmlFile(null);
    setExistingKmlFile(null);
  }

  const onCancel = () => {
    setShowConfirmDialog(false);
    setNewKmlFile(null);
    setExistingKmlFile(null);
  }

  // @ts-ignore
  return (
    <>
      <form style={styles.uploadForm}>
        <Typography variant="h1" sx={styles.uploadFormHeader}>
          Upload Data Set
        </Typography>
        <Typography sx={styles.dataTypes}>
          Upload your data (.tif, .tiff, .las, .laz, .kml, .kmz) or .zip to kick off your AirWorks Order.
        </Typography>
        <Box sx={styles.outputConfidenceBorder}>
          <ErrorIcon sx={styles.errorIcon} />
          <Typography sx={styles.outputConfidence}>
            Our outputs are only as good as the data you provide us! Please take a few minutes to review our
            <a style={styles.anchorTag} target="_blank" href="http://support.airworks.io/Layer-Output-Confidence-Level.2381381633.html" onClick={() => { window?.pendo?.track('Clicked Output Confidence Link', { projectId }); }} rel="noreferrer"> Layer Output Confidence Level.</a>
          </Typography>
          <Dialog sx={styles.dialogBox} open={openDialog}>
            <IconButton edge="end" sx={styles.closeButton} onClick={() => setOpenDialog(false)}>
              <CloseIcon />
            </IconButton>
            <DialogTitle sx={styles.dialogTitle}>
              Size Limit Reached!
            </DialogTitle>
            <DialogContent sx={styles.dialogContent}>
              <Typography>
                The maximum filesize AirWorks can currently handle is 20GB.
              </Typography>
              <Typography>
                If your dataset is larger than that, please tile it into smaller sections and upload those instead.
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button color="primary" size="small" onClick={() => setOpenDialog(false)}>OK</Button>
            </DialogActions>
          </Dialog>
        </Box>
        <DropzoneWrapper acceptedExtensions={acceptedExtensions} onDropAccepted={handleFileUpload}>
          {(getInputProps, open) => (
            <>
              <img src={uploadFilesIcon} alt="Upload files" />
              <input {...getInputProps()} />
              <Typography variant="h5" sx={{...styles.uploadFormText, ...styles.uploadFormTextTitle}}>
                Drag and drop to upload
              </Typography>
              <Typography variant="caption" sx={{...styles.uploadFormText, ...styles.uploadFormTextSubtitle}}>
                {'or '}
                <Button sx={styles.openFileDialogLink} onClick={open}>browse</Button>
                {' to choose a file (Size limit: 20GB per file)'}
              </Typography>
            </>
          )}
        </DropzoneWrapper>
        <Box sx={styles.uploadFormTextContainer}>
          <Typography variant="body1" sx={{...styles.formText, ...styles.whatToUpload}}>
            What to Upload:
          </Typography>
          <Typography variant="body1" sx={styles.formText}>
            <ul>
              <li>Area of interest (.kml / .kmz)</li>
              <li>2D data (.tif or .tiff).</li>
              <li>3D data (.laz recommended).</li>
              <li>Panoramic Images (.zip).</li>
              <li>Pole Images (.zip).</li>
            </ul>
          </Typography>
          <Typography variant="body1" sx={styles.formText}>
            Please provide panoramic images or pole images in a .zip folder containing only .jpg files and .txt metadata files. Avoid including subfolders or other file formats (such as .tif, .laz, etc.) within the zip.
          </Typography>
          <Typography variant="body1" sx={styles.formText}>
            There are some coordinate reference systems that we don’t yet support.
            <a href="https://awkb.scrollhelp.site/airworksknowledgebase/coordinate-reference-system-crs" style={styles.anchorTag} target="_blank" rel="noreferrer"> Look up your CRS here.</a>
          </Typography>
        </Box>
        <Box sx={styles.progressSectionsWrapper}>
          {
            uploadFiles.map((file) => (
              <Box key={file.id} sx={styles.progressSection}>
                <Box
                  sx={{ ...styles.fileExtension, ...getBackgroundByExtension(file.extension) }}
                >
                  <Typography variant="body1" sx={styles.fileExtensionText}>
                    {`.${file.extension}`}
                  </Typography>
                </Box>
                <Box sx={styles.progress}>
                  <Grid container spacing={1}>
                    <Grid item xs={4}>
                      {file.name.length > 18 ? (
                        <Tooltip title={file.name} placement="top" arrow>
                          <Typography variant="body1" sx={styles.progressText}>
                            {file.name}
                          </Typography>
                        </Tooltip>
                      ) : (
                        <Typography variant="body1" sx={styles.progressText}>
                          {file.name}
                        </Typography>
                      )}
                    </Grid>
                    <Grid item xs={4}>
                      <Box sx={styles.progressPercentageTextCenter}>
                        <Typography variant="body1">
                          {file.progress.toFixed(2)}
                          {' '}
                          %
                          {' '}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={4}>
                      <Box sx={styles.clearIconContainer}>
                        <IconButton sx={styles.progressButton} onClick={() => cancelUploadOrDeleteFile(file.progress, file.id, file.name)}>
                          <ClearIcon sx={styles.cancelIcon} />
                        </IconButton>
                      </Box>
                    </Grid>
                  </Grid>
                  <Box>
                    <LinearProgress
                      variant="determinate"
                      value={file.progress}
                      sx={getBackgroundByExtension(file.extension)}
                    />
                  </Box>
                </Box>
              </Box>
            ))
          }
        </Box>
        <Box sx={styles.uploadFilesActionsContainer}>
          <Box sx={{...styles.uploadFormTextContainer, ...styles.noDataMessage}}>
            <Typography variant="body1" sx={{...styles.formText, mt: 0}}>
              Need data? Explore the new
            </Typography>
            <a href="https://awkb.scrollhelp.site/airworksknowledgebase/the-airworks-data-marketplace" style={styles.anchorTag} target="_blank" rel="noreferrer"> AirWorks Data Marketplace!</a>
          </Box>
          {/* Always enable the continue button, but when a file is being uploaded wait for upload to finish */}
          <Button
            onClick={async () => {
              onSuccess();
            }}
            variant="outlined"
            disabled={(!uploadFiles.length && _ADMIN_) || (uploadFiles.length && !!allFilesUploaded)}
            sx={styles.uploadFormSubmit}
          >
            Continue
          </Button>
        </Box>
      </form>
      <ErrorDialog
        showDialog={showUnsupportedFiletypesDialog}
        onCancel={handleProceedUnsupportedFiletypes}
        onAccept={handleUploadDifferentFiles}
        titleText="Warning: Unsupported filetypes detected!"
        acceptText="Upload a different file"
        cancelText="Proceed"
        error={false}
      >
        <Typography variant="body1" sx={styles.epsgErrorMessage}>
          AirWorks accepts .jpg and .txt files as part of panoramic image and pole image uploads. 
          Any other filetypes or nested folder structure will be discarded as part of this Upload.
        </Typography>
      </ErrorDialog>
      <ErrorDialog
        showDialog={showNoFilesDialog}
        onCancel={() => setShowNoFilesDialog(false)}
        titleText="No files available for upload"
        error={true}
        cancelText='Close'
      >
        <Typography variant="body1" sx={styles.epsgErrorMessage}>
          The uploaded .zip file does not contain any supported files (.jpg or .txt).
          Please ensure that the .zip file contains only .jpg and .txt files without any nested folders.
        </Typography>
      </ErrorDialog>

      <ConfirmDialog
        showDialog={showConfirmDialog}
        onOk={onOk}
        onCancel={onCancel}
        contentText="Current .kml file will be removed"
        loading={loading}
      />

      <ZipTypeDialog
        showDialog={showZipTypeDialog}
        onConfirm={handleZipTypeConfirm}
        onCancel={handleZipTypeCancel}
      />
    </>
  );
};

MultipleFileUploadForm.propTypes = {
  onSuccess: PropTypes.func.isRequired,
};

export default MultipleFileUploadForm;
